import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  IconButton,
  Modal,
  Backdrop
} from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Podcasts from '@mui/icons-material/Podcasts';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import { havenColors } from '../../theme/theme';
import { Link as RouterLink } from 'react-router';
import LazyYouTube from '../../components/common/LazyYouTube';
import HeroVideo from '../../components/common/HeroVideo';
import { analytics } from '../../utils/analytics';

// Import media assets (WebP with fallbacks)
import Bundle from '../../assets/images/Bundle.webp';
import BundleFallback from '../../assets/images/Bundle.png';
import AppStoreButton from '../../assets/images/download-app-store.webp';
import AppStoreButtonFallback from '../../assets/images/download-app-store.png';
import GooglePlayButton from '../../assets/images/get-it-on-google-play.webp';
import GooglePlayButtonFallback from '../../assets/images/get-it-on-google-play.png';

import BackgroundMask from '../../assets/images/Mask-Group-24-1.webp';
import BackgroundMaskFallback from '../../assets/images/Mask-Group-24-1.png';
import BlueprintBackground from '../../assets/images/blueprint2-left-banner-scaled.jpg';
import CO2StatusCard, { IAQStatusCards } from '../../components/common/CO2StatusCard';
import HomeAudienceSplit from '../../components/sections/HomeAudienceSplit';

// Import credibility thumbnails (WebP with PNG fallback)
import IllusoryIdealsImage from '../../assets/images/illustoryideals.webp';
import IllusoryIdealsImageFallback from '../../assets/images/illustoryideals.png';
import AcServiceTechImage from '../../assets/images/acservicetech1.webp';
import AcServiceTechImageFallback from '../../assets/images/acservicetech1.png';
import HealthSupplementsImage from '../../assets/images/healthyairsupplements.webp';
import HealthSupplementsImageFallback from '../../assets/images/healthyairsupplements.png';

// Hero image is in public/ folder so it can be preloaded in HTML head before React loads
// This dramatically improves LCP since image download starts before React bundle loads
const HeroVideoThumbnail = '/hero-video-thumbnail.webp';

// Decorative floating-particle styles, generated once at module load.
// Uses a deterministic seeded pseudo-random (not Math.random) so the SSR
// prerender and the client produce identical markup — otherwise the
// positions differ and React reports a hydration mismatch. Keeping it at
// module scope also avoids calling an impure function during render.
const seededRandom = (seed: number): number => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};
const FLOATING_PARTICLES = Array.from({ length: 30 }, (_, i) => {
  const r = (n: number) => seededRandom(i * 10 + n);
  return {
    width: r(1) * 8 + 6,
    height: r(2) * 8 + 6,
    opacity: r(3) * 0.4 + 0.4,
    left: r(4) * 100,
    top: r(5) * 100,
    duration: r(6) * 5 + 8,
    delay: r(7) * 5,
    variant: i % 4,
  };
});

const HomePage: React.FC = () => {
  const [videoPlaying, setVideoPlaying] = useState(true); // Auto-play video on load
  // Start at the first real item (index 2, after the duplicated last items
  // used for the infinite-carousel illusion).
  const [currentSlide, setCurrentSlide] = useState(2);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVideoId, setModalVideoId] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Hero image preload is now in index.html head (loads before React!)
  // This ensures the image starts downloading immediately, not after React bundle loads

  const handleVideoClick = () => {
    setVideoPlaying(true);
  };

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // Handle credibility item click
  const handleCredibilityItemClick = (item: any) => {
    if (item.type === 'video' && item.url.includes('youtube.com')) {
      const videoId = getYouTubeVideoId(item.url);
      if (videoId) {
        setModalVideoId(videoId);
        setModalOpen(true);
        return;
      }
    }
    // For non-YouTube videos and podcasts, open in new tab
    window.open(item.url, '_blank');
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalVideoId(null);
  };

  // Credibility content - real media coverage, podcasts, press releases
  const credibilityItems = [
    {
      type: 'podcast',
      title: 'Healthy Air Supplements vs. Pillars w/ Kevin Hart',
      source: 'HVAC School',
      date: '2024',
      icon: <Podcasts />,
      url: 'https://www.hvacrschool.com/podcasts/healthy-air-supplements-vs-pillars-w-kevin-hart/',
      thumbnail: HealthSupplementsImage,
      thumbnailFallback: HealthSupplementsImageFallback
    },
    {
      type: 'video',
      title: 'CEO of Haven IAQ shares secrets homeowners need to know about HVAC!',
      source: 'HVAC Guide For Homeowners',
      date: '2024',
      icon: <PlayArrow />,
      url: 'https://www.youtube.com/watch?v=zgHiXXFST3s',
      thumbnail: 'https://i.ytimg.com/vi_webp/zgHiXXFST3s/hqdefault.webp'
    },
    {
      type: 'video',
      title: 'Indoor Air Quality Monitoring Solution with HAVEN - LIVE from AHR',
      source: 'HVAC School',
      date: '2024',
      icon: <PlayArrow />,
      url: 'https://www.youtube.com/watch?v=WIsWSIMVZDs',
      thumbnail: 'https://i.ytimg.com/vi_webp/WIsWSIMVZDs/hqdefault.webp'
    },
    {
      type: 'podcast',
      title: 'Cracking the Home Health Comfort Code: IAQ\'s Illusory Ideals',
      source: 'HVAC School',
      date: '2024',
      icon: <Podcasts />,
      url: 'https://www.hvacrschool.com/podcasts/cracking-the-home-health-comfort-code-iaqs-illusory-ideals/',
      thumbnail: IllusoryIdealsImage,
      thumbnailFallback: IllusoryIdealsImageFallback
    },
    {
      type: 'video',
      title: 'Changing The Way We Think About IAQ Controls',
      source: 'ESCO Institute-HVAC Excellence',
      date: '2024',
      icon: <PlayArrow />,
      url: 'https://www.youtube.com/watch?v=Uk-fV9AKf0M',
      thumbnail: 'https://i.ytimg.com/vi_webp/Uk-fV9AKf0M/hqdefault.webp'
    },
    {
      type: 'podcast',
      title: 'Discussing Indoor Air Quality With Kevin Hart From Haven IAQ!',
      source: 'AC Service Tech Answers Podcast',
      date: '2024',
      icon: <Podcasts />,
      url: 'https://metacast.app/podcast/ac-service-tech-answers-podcast/tbSzqUMr/discussing-indoor-air-quality-with-kevin-hart-from-haven-iaq/YXczkQUD',
      thumbnail: AcServiceTechImage,
      thumbnailFallback: AcServiceTechImageFallback
    }
  ];

  // Create infinite carousel by duplicating items
  const extendedItems = [
    ...credibilityItems.slice(-2), // Last 2 items at the beginning
    ...credibilityItems,
    ...credibilityItems.slice(0, 2)  // First 2 items at the end
  ];

  // Card slider navigation with infinite loop
  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => prev + 1);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => prev - 1);
  };

  // Handle infinite loop transitions
  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        
        // Reset position for infinite loop
        if (currentSlide >= credibilityItems.length + 2) {
          setCurrentSlide(2); // Jump to real first item
        } else if (currentSlide < 2) {
          setCurrentSlide(credibilityItems.length + 1); // Jump to real last item
        }
      }, 600); // Match transition duration

      return () => clearTimeout(timer);
    }
  }, [currentSlide, isTransitioning, credibilityItems.length]);

  // Auto-advance slides every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        setCurrentSlide(prev => prev + 1);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [isTransitioning]);




 

  return (
    <Box>
      {/* Hero Section - Matching Original Design */}
      <Box
        sx={{
                      minHeight: '45vh', // Further reduced for more compact hero section
          background: `linear-gradient(45deg, ${havenColors.primaryYellow} 0%, ${havenColors.primaryTeal} 70%)`, // 45deg: Yellowflow (left-lower) to Blewstream (right-upper)
          position: 'relative',
          overflow: 'visible', // Changed to visible to allow video to extend beyond bounds
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `image-set(url(${BackgroundMask}) type('image/webp'), url(${BackgroundMaskFallback}))`,
            backgroundSize: 'auto 100%', // Use natural width, scale height to fit container
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'left center', // Focus on left side to avoid cutting off the person
            opacity: 0.08,
            zIndex: 1
          }
        }}
      >
        {/* Floating Bubble Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '12%',
            right: '12%',
            width: 50,
            height: 50,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
            animation: 'float 6s ease-in-out infinite',
            zIndex: 3
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '28%',
            right: '22%',
            width: 35,
            height: 35,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            animation: 'float 4s ease-in-out infinite reverse',
            zIndex: 3
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '45%',
            right: '8%',
            width: 70,
            height: 70,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            animation: 'float 8s ease-in-out infinite',
            zIndex: 3
          }}
        />

        {/* Mobile Layout - Stacked with centered content */}
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
          alignItems: 'center',
                justifyContent: 'center',
          '@media (min-width: 700px)': {
            display: 'none'
          },
          position: 'relative',
          zIndex: 2,
          py: 3,
          px: 2,
          textAlign: 'center',
          width: '100%'
              }}>
                <Typography 
                  variant="h1" 
                  sx={{ 
              fontSize: '2rem',
                    fontWeight: 700,
                    mb: 2,
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              color: 'white'
                  }}
                >
                  Your home, a safe haven.
                </Typography>
                
                <Typography 
                  variant="h5" component="p" 
                  sx={{ 
                    mb: 3, 
              fontWeight: 600,
              fontSize: '1rem',
              opacity: 1,
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              color: 'white'
                  }}
                >
                  HAVEN helps you achieve healthy indoor air.
                </Typography>


          {/* Video for mobile */}
          <Box sx={{ 
                width: '100%',
            maxWidth: '500px',
            mx: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
            }}>
              <Box 
                sx={{ 
                  position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                aspectRatio: '16/9',
                width: '100%',
                maxWidth: '500px'
              }}
            >
              {!videoPlaying ? (
                <Box
                  onClick={handleVideoClick}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%'
                  }}
                >
                  <Box width={2560} height={814}
                    component="img"
                    src={HeroVideoThumbnail}
                    alt="Introducing HAVEN - Video Thumbnail"
                    fetchPriority="high"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: 2,
                      aspectRatio: '2560 / 814'
                    }} />
                  
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      width: 50,
                      height: 50,
                  display: 'flex',
                  alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <PlayArrow 
                      sx={{ 
                        fontSize: 30,
                        color: havenColors.primaryTeal,
                        ml: 0.5
                      }} 
                    />
                  </Box>
                </Box>
              ) : (
                <HeroVideo
                  poster={HeroVideoThumbnail}
                  autoplay={true}
                  width="100%"
                  height="100%"
                  sx={{
                    borderRadius: 2
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Desktop Layout - Side by side with centered content */}
        <Box sx={{ 
          display: 'none',
          '@media (min-width: 700px)': {
            display: 'flex'
          },
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          position: 'relative', 
          zIndex: 2, 
                  width: '100%',
          px: { sm: 2, md: 4, lg: 6, xl: 8 },
          py: { sm: 4, md: 0 }
        }}>
          {/* Content Container - perfectly centered */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: { sm: '800px', md: '1000px', lg: '1200px', xl: '1400px' },
            gap: { sm: 1, md: 2, lg: 4, xl: 6 },
            flexDirection: 'row',
            mx: 'auto' // Ensures perfect centering
          }}>
            {/* Left Side - Text Content */}
            <Box sx={{ 
              flex: '1',
              color: 'white', 
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              px: { sm: 1, md: 2, lg: 3, xl: 4 },
              minWidth: 0 // Prevent flex item from overflowing
            }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { 
                    sm: '1.8rem',
                    md: '2rem',
                    lg: '2.6rem',
                    xl: '3.2rem'
                  },
                  fontWeight: 700,
                  mb: 2,
                  lineHeight: 1.1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Your home, a safe haven.
              </Typography>
              
              <Typography 
                variant="h5" component="p" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  fontSize: { 
                    sm: '0.9rem',
                    md: '1rem',
                    lg: '1.1rem',
                    xl: '1.2rem'
                  },
                  opacity: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                HAVEN helps you achieve healthy indoor air.
              </Typography>

            </Box>

            {/* Right Side - Video Content */}
            <Box sx={{ 
              flex: '1',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0 // Prevent flex item from overflowing
            }}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                  overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    zIndex: 4,
                    width: '100%',
                    maxWidth: { 
                    sm: 290,
                    md: 340,
                    lg: 460,
                    xl: 580
                    },
                  aspectRatio: '16/9',
                  transform: 'perspective(1000px) rotateY(-3deg)', // Always 3D in desktop
                    '&:hover': {
                    transform: 'perspective(1000px) rotateY(0deg) scale(1.05)'
                    }
                  }}
                >
                  {!videoPlaying ? (
                  // Custom Thumbnail
                    (<Box
                      onClick={handleVideoClick}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: 3,
                      overflow: 'hidden',
                      width: '100%',
                      height: '100%'
                      }}
                    >
                  <Box width={2560} height={814}
                        component="img"
                        src={HeroVideoThumbnail}
                        alt="Introducing HAVEN - Video Thumbnail"
                        fetchPriority="high"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          borderRadius: 3,
                          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',

                          '&:hover': {
                          boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
                          },

                          transition: 'box-shadow 0.3s ease',
                          aspectRatio: '2560 / 814'
                        }} />
                      {/* Play Button Overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: '50%',
                        width: { sm: 40, md: 50, lg: 60, xl: 70 },
                        height: { sm: 40, md: 50, lg: 60, xl: 70 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          '&:hover': {
                            backgroundColor: 'white',
                            transform: 'translate(-50%, -50%) scale(1.1)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <PlayArrow 
                          sx={{ 
                          fontSize: { sm: 20, md: 24, lg: 28, xl: 36 },
                            color: havenColors.primaryTeal,
                          ml: 0.5
                          }} 
                        />
                      </Box>
                    </Box>)
                  ) : (
                  // Self-hosted hero video (native <video>, no YouTube player JS)
                    (<HeroVideo
                      poster={HeroVideoThumbnail}
                      autoplay={true}
                      width="100%"
                      height="100%"
                      sx={{
                        borderRadius: 3,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                        display: 'block'
                      }}
                    />)
                  )}
                </Box>
              </Box>
          </Box>
      </Box>


      </Box>
      {/* Customer Segments Section - Improved Responsive Layout */}
      <Container maxWidth="xl" sx={{ py: 8, px: { xs: 2, sm: 3, md: 4 } }}>
        <HomeAudienceSplit />
      </Container>
      {/* Meet HAVEN Section */}
      <Box sx={{ 
        py: 7.5, 
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${BlueprintBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.99,
          zIndex: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, rgba(69, 90, 100, 0.8) 0%, rgba(69, 90, 100, 1) 100%)`,
          zIndex: 2
        }
      }}>
        <Container maxWidth="lg" sx={{ 
          position: 'relative', 
          zIndex: 3,
          px: { xs: 2, sm: 3, md: 4 }
        }}>
          <Grid container spacing={4} sx={{
            alignItems: "center"
          }}>
            <Grid
              size={{
                xs: 12,
                md: 5
              }}>
              <Box
                component="picture"
                sx={{
                  width: '100%',
                  maxWidth: 450,
                  mx: 'auto',
                  display: 'block'
                }}
              >
                <source srcSet={Bundle} type="image/webp" />
                  <Box width={450} height={300}
                  component="img"
                  src={BundleFallback}
                  alt="HAVEN Monitors and Controller"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: 'auto'
                  }} />
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 7
              }}>
              <Box sx={{ 
                pl: { md: 2 },
                textAlign: { xs: 'center', md: 'left' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' }
              }}>
                <Typography variant="h3" component="h2" sx={{ 
                  mb: 3, 
                  fontWeight: 700, 
                  color: 'white',
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}>
                  Meet HAVEN.
                </Typography>
                <Typography variant="h4" component="p" sx={{ 
                  mb: 4, 
                  color: 'white',
                  fontWeight: 400,
                  lineHeight: 1.4,
                  fontSize: { xs: '1.1rem', md: '1.3rem' }
                }}>
                  Our solution for whole-home indoor air quality management: for peace of mind, and clean air services year round.
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowForward />}
                  component={RouterLink}
                  to="/room-monitor"
                  sx={{
                    borderColor: havenColors.primaryTeal,
                    color: havenColors.primaryTeal,
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: havenColors.primaryTeal,
                      color: 'white',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  MORE ON SOLUTIONS
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* Media Coverage & Credibility Section */}
      <Box sx={{ 
        py: 8, 
        backgroundColor: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Subtle background pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(circle at 25% 25%, ${havenColors.primaryTeal} 2px, transparent 2px), radial-gradient(circle at 75% 75%, ${havenColors.primaryYellow} 2px, transparent 2px)`,
          backgroundSize: '60px 60px',
          backgroundPosition: '0 0, 30px 30px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" sx={{ 
              mb: 2, 
              fontWeight: 700, 
              color: havenColors.darkGray,
              fontSize: { xs: '1.8rem', md: '2.2rem' }
            }}>
              Trusted by Industry Leaders
            </Typography>
          </Box>

          {/* Card Slider Carousel */}
          <Box sx={{ position: 'relative' }}>
            {/* Navigation Arrows */}
            <IconButton
              onClick={prevSlide}
              aria-label="Previous slide"
              sx={{
                position: 'absolute',
                left: { xs: -15, md: -25 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                backgroundColor: 'white',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                width: 48,
                height: 48,
                '&:hover': {
                  backgroundColor: havenColors.primaryTeal,
                  color: 'white',
                  transform: 'translateY(-50%) scale(1.1)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <ChevronLeft />
            </IconButton>
            
            <IconButton
              onClick={nextSlide}
              aria-label="Next slide"
              sx={{
                position: 'absolute',
                right: { xs: -15, md: -25 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                backgroundColor: 'white',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                width: 48,
                height: 48,
                '&:hover': {
                  backgroundColor: havenColors.primaryTeal,
                  color: 'white',
                  transform: 'translateY(-50%) scale(1.1)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <ChevronRight />
            </IconButton>

            {/* Slider Container */}
            <Box sx={{ 
              overflow: 'hidden',
              px: { xs: 3, md: 5 }
            }}>
              <Box sx={{
                display: 'flex',
                transform: {
                  xs: `translateX(-${currentSlide * 100}%)`, // 1 card on mobile
                  sm: `translateX(-${currentSlide * 55}%)`,  // wider cards on tablet
                  md: `translateX(-${currentSlide * 38}%)` // wider cards on desktop
                },
                transition: isTransitioning ? 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
                gap: 3
              }}>
                {extendedItems.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      minWidth: { xs: '100%', sm: '55%', md: '38%' },
                      px: 1
                    }}
                  >
                    <Card
                      onClick={() => handleCredibilityItemClick(item)}
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        border: '1px solid rgba(0,0,0,0.08)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                        }
                      }}
                    >
                      {/* Thumbnail Image */}
                      <Box sx={{
                        position: 'relative',
                        height: 220,
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {/* Lazy-loaded thumbnail — this carousel is below the
                            fold, so the images shouldn't block first paint. */}
                        {item.thumbnail && (
                          <Box
                            component="img"
                            src={item.thumbnail}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            decoding="async"
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        )}
                        {/* Overlay for better text readability */}
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: item.thumbnail ? 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)' : 'none'
                        }} />
                        
                        {/* Play button for videos */}
                        {item.type === 'video' && (
                          <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '50%',
                            width: 60,
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2
                          }}>
                            <PlayArrow sx={{ 
                              fontSize: 32,
                              color: havenColors.primaryTeal,
                              ml: 0.5
                            }} />
                          </Box>
                        )}

                        {/* Type Icon for podcasts */}
                        {item.type === 'podcast' && (
                          <Box sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: 2,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            zIndex: 2
                          }}>
                            <Podcasts sx={{ 
                              fontSize: 20,
                              color: 'white'
                            }} />
                          </Box>
                        )}


                        {/* Fallback content if no thumbnail */}
                        {!item.thumbnail && (
                          <Box sx={{ 
                            background: item.source.includes('AC Service Tech') 
                              ? 'linear-gradient(135deg, #2E2E5F 0%, #4A4A8A 100%)'
                              : `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.darkTeal} 100%)`,
                            color: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            height: '100%',
                            p: 3
                          }}>
                            {item.icon}
                            <Typography variant="h6" component="h3" sx={{ 
                              color: 'white',
                              fontWeight: 600,
                              textAlign: 'center',
                              fontSize: '0.9rem'
                            }}>
                              {item.source}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              color: 'rgba(255,255,255,0.8)',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '1px'
                            }}>
                              {item.type}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      <CardContent sx={{ p: 2.5 }}>
                        {/* Source and Date */}
                        <Typography variant="caption" sx={{ 
                          color: 'text.secondary',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          mb: 1,
                          display: 'block'
                        }}>
                          {item.source} • {item.date}
                        </Typography>

                        {/* Title */}
                        <Typography variant="h6" component="h3" sx={{ 
                          fontWeight: 600, 
                          color: havenColors.darkGray,
                          lineHeight: 1.3,
                          fontSize: '1rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {item.title}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Slide Indicators */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 4,
              gap: 1
            }}>
              {credibilityItems.map((_, index) => {
                // Calculate the actual position considering the infinite loop
                const actualIndex = (currentSlide - 2 + credibilityItems.length) % credibilityItems.length;
                const isActive = index === actualIndex;
                
                return (
                  <Box
                    key={index}
                    onClick={() => {
                      if (!isTransitioning) {
                        setIsTransitioning(true);
                        setCurrentSlide(index + 2); // Offset by 2 for duplicated items
                      }
                    }}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: isActive ? havenColors.primaryTeal : 'rgba(0,0,0,0.2)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: havenColors.primaryTeal,
                        transform: 'scale(1.2)'
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Container>
      </Box>
      {/* Ready to Get Started Section */}
      <Box sx={{ 
        py: 8, 
        background: `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.darkTeal} 100%)`,
        color: 'white'
      }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            sx={{
              alignItems: "center",
              justifyContent: "center"
            }}>
            <Grid
              size={{
                xs: 12,
                md: 7
              }}>
              <Typography variant="h3" component="h2" sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                textAlign: { xs: 'center', md: 'left' }
              }}>
                Ready to get started with HAVEN?
              </Typography>
              <Typography variant="h5" component="p" sx={{ 
                mb: 4, 
                opacity: 0.9,
                fontWeight: 400,
                lineHeight: 1.5,
                textAlign: { xs: 'center', md: 'left' }
              }}>
                The first step of every HAVEN journey is to download the HAVEN IAQ app.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-start' }
              }}>
                <Box
                  component="a"
                  href="https://apps.apple.com/us/app/haven-iaq/id1527338018"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => analytics.trackAppDownloadClick('ios', 'home_ready_to_get_started', 'https://apps.apple.com/us/app/haven-iaq/id1527338018')}
                  sx={{ display: 'block' }}
                >
                  <Box
                    component="picture"
                    sx={{
                      height: 60,
                      width: 'auto',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    <source srcSet={AppStoreButton} type="image/webp" />
                  <Box width={120} height={60}
                      component="img"
                      src={AppStoreButtonFallback}
                      alt="Download on the App Store"
                      loading="lazy"
                      sx={{
                        height: 60,
                        width: 'auto'
                      }} />
                  </Box>
                </Box>
                <Box
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.tzoa.haveniaq"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => analytics.trackAppDownloadClick('android', 'home_ready_to_get_started', 'https://play.google.com/store/apps/details?id=com.tzoa.haveniaq')}
                  sx={{ display: 'block' }}
                >
                  <Box
                    component="picture"
                    sx={{
                      height: 60,
                      width: 'auto',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    <source srcSet={GooglePlayButton} type="image/webp" />
                  <Box width={120} height={60}
                      component="img"
                      src={GooglePlayButtonFallback}
                      alt="Get it on Google Play"
                      loading="lazy"
                      sx={{
                        height: 60,
                        width: 'auto'
                      }} />
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                borderRadius: 2,
                minHeight: '400px',
                width: '100%',
                padding: 1,
                overflow: 'hidden'
              }}
              size={{
                xs: 12,
                md: 5
              }}>
              {/* Lazy-loaded backdrop image. Was a CSS background-image of a
                  140 KB PNG whose filename contained a U+202F narrow no-break
                  space (a macOS-screenshot artefact) — so the URL the browser
                  built never matched the file on disk and Netlify served the
                  243 KB SPA fallback instead. Now an actual <img>, slugged,
                  served as 11 KB WebP, and properly lazy-loaded. */}
              <Box
                component="img"
                src="/homepage-preview.webp"
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  zIndex: 0,
                }}
              />
              {/* Background IAQ Status Cards Grid */}
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                zIndex: 1,
                opacity: 0.6,
                filter: 'blur(2px)'
              }}>
                <IAQStatusCards />
              </Box>
              
              {/* Foreground Animated CO2 Card with Glassmorphism */}
              <Box sx={{
                position: 'relative',
                zIndex: 2,
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)', // Safari support
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                padding: 1,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 'inherit',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
                  pointerEvents: 'none',
                  zIndex: -1
                }
              }}>
                <CO2StatusCard />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* HAVEN Blog & News Section */}
      <Box sx={{ 
        py: 10, 
        backgroundColor: '#f8f9fa',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Floating Air Particles Background */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 1
        }}>
          {/* Generate multiple floating air particles */}
          {FLOATING_PARTICLES.map((p, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: `${p.width}px`,
                height: `${p.height}px`,
                backgroundColor: `rgba(255, 230, 35, ${p.opacity})`,
                borderRadius: '50%',
                left: `${p.left}%`,
                top: `${p.top}%`,
                animation: `floatAirParticle${p.variant} ${p.duration}s linear infinite`,
                animationDelay: `${p.delay}s`
              }}
            />
          ))}
        </Box>
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="h3" component="h2" align="center" sx={{ 
            mb: 6, 
            fontWeight: 700, 
            color: havenColors.darkGray,
            fontSize: { xs: '2rem', md: '2.5rem' }
          }}>
            HAVEN Blog & News
          </Typography>
          
          <Grid container spacing={4}>
            {[
              {
                title: "No One CAREs About Residential Controls – An Open Letter",
                author: "Kevin Hart",
                date: "September 18, 2024",
                excerpt: "Let's face it: No one CAREs about residential controls - and that needs to change. The current state of residential HVAC controls, especially when it comes to Indoor Air Quality (IAQ), is flat-out inadequate.",
                category: "Industry Insights",
                slug: "no-one-cares-about-residential-controls",
                hasInternalPage: true
              },
              {
                title: "HAVEN and Ecosense Radon Solutions",
                author: "HAVEN Team",
                date: "March 6, 2024",
                excerpt: "How HAVEN and Ecosense combine radon awareness with ventilation and indoor air quality monitoring for healthier homes.",
                category: "Health & Safety",
                slug: "haven-and-ecosense-radon-solutions",
                hasInternalPage: true
              },
              {
                title: "Health effects of Particulate Matter",
                author: "Lucy C",
                date: "January 11, 2022",
                excerpt: "When the air that we breathe is compromised (eg. there are too many particles in the air), we can feel sluggish or tired. Fine particulate matter is particularly dangerous, as particles of 2.5UG or lower are small enough to enter directly into our organs!",
                category: "Health & Safety",
                url: "https://haveniaq.com/2022/01/health-effects-of-particulate-matter/",
                hasInternalPage: false
              }
            ].map((post, index) => {
              const handlePostClick = () => {
                if (post.hasInternalPage) {
                  window.location.href = `/blog/${post.slug}`;
                } else {
                  window.open(post.url, '_blank');
                }
              };
              
              return (
                <Grid
                  key={index}
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Card 
                    onClick={handlePostClick}
                    sx={{ 
                      height: '100%', 
                      borderRadius: 3,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                      }
                    }}>
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{
                        // tealText (darker) so white label clears 4.5:1.
                        // Brand teal #00C4C1 only hit ~2.17:1 with white.
                        backgroundColor: havenColors.tealText,
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        alignSelf: 'flex-start',
                        mb: 2
                      }}>
                        {post.category}
                      </Box>
                      <Typography variant="h6" component="h3" sx={{ 
                        mb: 2, 
                        fontWeight: 600, 
                        color: havenColors.darkGray,
                        lineHeight: 1.3,
                        flex: '0 0 auto'
                      }}>
                        {post.title}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        mb: 3, 
                        color: 'text.secondary',
                        lineHeight: 1.5,
                        flex: '1 1 auto'
                      }}>
                        {post.excerpt}
                      </Typography>
                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="caption" sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.85rem'
                        }}>
                          By {post.author} • {post.date}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              size="large"
              href="/resources"
              sx={{
                borderColor: havenColors.primaryTeal,
                color: havenColors.primaryTeal,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: havenColors.primaryTeal,
                  color: 'white'
                }
              }}
            >
              View All Articles
            </Button>
          </Box>
        </Container>
      </Box>
      {/* Video Modal */}
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        slots={{
          backdrop: Backdrop
        }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
          }
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
          maxWidth: '1000px',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 0,
          outline: 'none'
        }}>
          {/* Close Button */}
          <IconButton
            onClick={handleModalClose}
            aria-label="Close video"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <Close />
          </IconButton>

          {/* Video Container */}
          {modalVideoId && (
            <Box sx={{
              position: 'relative',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
              borderRadius: 3
            }}>
              <LazyYouTube
                videoId={modalVideoId}
                title="YouTube Video"
                autoplay={true}
                width="100%"
                height="100%"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  borderRadius: 3
                }}
              />
            </Box>
          )}
        </Box>
      </Modal>
      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          @keyframes floatAirParticle0 {
            0% { transform: translate(0px, 0px); }
            25% { transform: translate(40px, -50px); }
            50% { transform: translate(-30px, -100px); }
            75% { transform: translate(-50px, -60px); }
            100% { transform: translate(0px, 0px); }
          }
          
          @keyframes floatAirParticle1 {
            0% { transform: translate(0px, 0px); }
            33% { transform: translate(-40px, -80px); }
            66% { transform: translate(50px, -40px); }
            100% { transform: translate(0px, 0px); }
          }
          
          @keyframes floatAirParticle2 {
            0% { transform: translate(0px, 0px); }
            20% { transform: translate(25px, -60px); }
            40% { transform: translate(-20px, -120px); }
            60% { transform: translate(-45px, -80px); }
            80% { transform: translate(35px, -30px); }
            100% { transform: translate(0px, 0px); }
          }
          
          @keyframes floatAirParticle3 {
            0% { transform: translate(0px, 0px); }
            50% { transform: translate(-35px, -90px); }
            100% { transform: translate(0px, 0px); }
          }
        `}
      </style>
    </Box>
  );
};

export default HomePage; 
